import { useCallback, useEffect, useRef } from 'react';

declare global {
  interface Window {
    __GOOGLE_OAUTH_CLIENT_ID__?: string;
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          prompt: () => void;
        };
      };
    };
  }
}

export function useGoogleAuth(onCredential: (credential: string) => void) {
  const initializedRef = useRef(false);
  const callbackRef = useRef(onCredential);
  callbackRef.current = onCredential;

  useEffect(() => {
    const clientId = window.__GOOGLE_OAUTH_CLIENT_ID__;
    if (!clientId || initializedRef.current) return;

    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      if (window.google?.accounts?.id) {
        clearInterval(interval);
        initializedRef.current = true;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => callbackRef.current(response.credential),
        });
      } else if (attempts >= 50) {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  const prompt = useCallback(() => {
    window.google?.accounts?.id?.prompt();
  }, []);

  const isAvailable =
    typeof window !== 'undefined' && !!window.__GOOGLE_OAUTH_CLIENT_ID__;

  return { prompt, isAvailable };
}
